import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { VaultItem } from '@heybesafe/shared';
import { useSafe } from '../../context/SafeContext';
import { useFamilyCategories } from '../../hooks/useFamilyCategories';
import { ContactAvatar } from '../../components/ContactAvatar';
import { HbsSiteIcon } from '../../components/hbs/SiteIcon';
import { HbsButton } from '../../components/hbs/Button';
import { Icon, type IconName } from '../../components/hbs/Icon';
import { describeRecurrence } from '../../binder/recurrence';

/**
 * A family Binder section — either a specific category or the "Family contacts"
 * catch-all — reached from the sidebar's collapsible family node. Read-only
 * browse of the family's shared items in that section, grouped by type.
 *
 * MVP scope: this renders the section shell + the (currently-rare) matching
 * items. The rich three-pane editor experience — contact/document/schedule
 * detail, photo capture, in-list editing — is the separate Binder-surface track
 * (nav-decisions §4). Today most families have no Binder items yet, so the
 * common state here is the empty state, which is the point: the rail links land
 * somewhere real instead of 404ing.
 */
// Section order: documents first, then contacts, then schedules. Sites (logins)
// aren't a Binder-native type — they trail at the end when present.
const TYPE_GROUPS: { key: VaultItem['type']; label: string; icon: IconName }[] = [
  { key: 'document', label: 'Documents', icon: 'note' },
  { key: 'list', label: 'Lists', icon: 'checklist' },
  { key: 'contact', label: 'Contacts', icon: 'contact' },
  { key: 'schedule', label: 'Schedules', icon: 'calendar' },
  { key: 'login', label: 'Sites', icon: 'login' },
];

/** Row supporting text for a list: "N of M done" (or an empty-state hint). */
function listSummary(item: VaultItem): string {
  const live = (item.listItems ?? []).filter((e) => !e.deleted);
  if (live.length === 0) return 'No items yet';
  const done = live.filter((e) => e.done).length;
  return `${done} of ${live.length} done`;
}

// Above this count a section gains a filter box and 5-per-page pagination so a
// large category (many documents, a long contact list) stays scannable.
const PAGE_SIZE = 5;

/**
 * One type group in the Binder: a collapsible accordion (open by default). Once
 * it holds more than PAGE_SIZE items it grows a name filter and pagination so
 * the section doesn't run off the page.
 */
function BinderSection({
  label,
  icon,
  items,
  renderRow,
}: {
  label: string;
  icon: IconName;
  items: VaultItem[];
  renderRow: (item: VaultItem) => JSX.Element;
}): JSX.Element {
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);

  const hasTools = items.length > PAGE_SIZE;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
  }, [items, filter]);

  // Reset to the first page whenever the filter narrows the list.
  useEffect(() => {
    setPage(0);
  }, [filter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const visible = hasTools ? filtered.slice(start, start + PAGE_SIZE) : filtered;
  const paginated = hasTools && filtered.length > PAGE_SIZE;

  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        id="binder-section-toggle-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute text-left"
      >
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} />
        <Icon name={icon} size={14} />
        {label}
        <span className="font-normal normal-case tracking-normal">({items.length})</span>
      </button>

      {open && (
        <>
          {hasTools && (
            <div className="flex items-center gap-2 h-9 px-2.5 bg-hbs-surface border border-hbs-border rounded-hbs-sm">
              <Icon name="search" size={14} className="text-hbs-text-mute" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={`Filter ${label.toLowerCase()}`}
                aria-label={`Filter ${label.toLowerCase()}`}
                className="flex-1 min-w-0 bg-transparent text-[13px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none"
              />
            </div>
          )}

          {visible.length === 0 ? (
            <p className="text-[12px] text-hbs-text-mute px-3 py-2">No matches.</p>
          ) : (
            <ul className="flex flex-col gap-px">{visible.map(renderRow)}</ul>
          )}

          {paginated && (
            <div className="flex items-center justify-between text-[12px] text-hbs-text-mute px-1">
              <span>
                {start + 1}–{Math.min(filtered.length, start + PAGE_SIZE)} of {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
              id="binder-prev-page-button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={safePage === 0}
                  aria-label="Previous page"
                  className="p-1 rounded-hbs-sm hover:bg-hbs-surface-alt disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  <Icon name="chevronLeft" size={16} />
                </button>
                <span>
                  Page {safePage + 1} of {pageCount}
                </span>
                <button
                  type="button"
              id="binder-next-page-button"
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={safePage >= pageCount - 1}
                  aria-label="Next page"
                  className="p-1 rounded-hbs-sm hover:bg-hbs-surface-alt disabled:opacity-40 disabled:hover:bg-transparent"
                >
                  <Icon name="chevronRight" size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default function FamilyBinderPage({ section }: { section?: 'contacts' }): JSX.Element {
  const { orgId, categoryId } = useParams<{ orgId: string; categoryId?: string }>();
  const { items, orgs = [] } = useSafe();
  const categoriesByOrg = useFamilyCategories();
  const navigate = useNavigate();

  const family = orgs.find((o) => o.id === orgId);
  const isContacts = section === 'contacts';
  const category = isContacts
    ? undefined
    : (orgId ? categoriesByOrg.get(orgId) ?? [] : []).find((c) => c.categoryId === categoryId);

  const title = isContacts ? 'Family contacts' : category?.name ?? 'Category';

  const shown = useMemo(() => {
    const familyItems = items.filter((i) => i.orgId === orgId);
    return isContacts
      ? familyItems.filter((i) => i.type === 'contact' && !i.categoryId)
      : familyItems.filter((i) => i.categoryId === categoryId);
  }, [items, orgId, categoryId, isContacts]);

  const groups = TYPE_GROUPS.map((g) => ({
    ...g,
    items: shown.filter((i) => i.type === g.key),
  })).filter((g) => g.items.length > 0);

  // Documents + schedules are born in a category (not the contacts catch-all).
  const canAdd = !isContacts && !!categoryId;

  function renderRow(item: VaultItem): JSX.Element {
    const leading = item.type === 'contact'
      ? <ContactAvatar name={item.name} size={30} />
      : item.type === 'document'
        ? <span className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-hbs-sm bg-hbs-surface-alt text-hbs-text-mute"><Icon name="note" size={16} /></span>
        : item.type === 'schedule'
          ? <span className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-hbs-sm bg-hbs-surface-alt text-hbs-text-mute"><Icon name="calendar" size={16} /></span>
          : item.type === 'list'
            ? <span className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-hbs-sm bg-hbs-surface-alt text-hbs-text-mute"><Icon name="checklist" size={16} /></span>
            : <HbsSiteIcon domain={item.name} size={30} />;
    // Contacts show their first phone/email; schedules show their recurrence
    // summary; documents show nothing extra.
    const supporting = item.type === 'contact'
      ? [item.phones?.find((p) => p.value.trim())?.value, item.emails?.find((e) => e.value.trim())?.value]
          .filter(Boolean).join(' · ')
      : item.type === 'schedule'
        ? describeRecurrence(item.rrule)
        : item.type === 'list'
          ? listSummary(item)
          : '';
    // Documents, contacts, and schedules open their detail. Contacts route
    // through the category when filed, else the contacts catch-all.
    const open = item.type === 'document' && categoryId
      ? () => navigate(`/orgs/${orgId}/binder/${categoryId}/documents/${item.id}`)
      : item.type === 'schedule' && categoryId
        ? () => navigate(`/orgs/${orgId}/binder/${categoryId}/schedules/${item.id}`)
        : item.type === 'list' && categoryId
          ? () => navigate(`/orgs/${orgId}/binder/${categoryId}/lists/${item.id}`)
          : item.type === 'contact'
            ? () => navigate(categoryId
                ? `/orgs/${orgId}/binder/${categoryId}/contacts/${item.id}`
                : `/orgs/${orgId}/binder/contacts/${item.id}`)
            : undefined;
    return (
      <li key={item.id}>
        <div
          id={`binder-row-${item.id}`}
          {...(open ? { role: 'button', tabIndex: 0, onClick: open, onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } } } : {})}
          className={`flex items-center gap-3 px-3 py-2 rounded-hbs-md ${open ? 'cursor-pointer hover:bg-hbs-surface-alt' : ''}`}
        >
          {leading}
          <div className="flex flex-col min-w-0">
            <span className="text-[14px] text-hbs-text truncate">{item.name}</span>
            {supporting && (
              <span className="text-[12px] text-hbs-text-mute truncate">{supporting}</span>
            )}
          </div>
        </div>
      </li>
    );
  }

  return (
    <div className="p-7 flex flex-col gap-6 max-w-3xl">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">
            {family?.name ?? 'Family'}
          </p>
          <h1 className="font-display font-semibold text-[30px] tracking-hbs-tighter text-hbs-text">
            {title}
          </h1>
        </div>
        {canAdd && (
          <div className="flex gap-2">
            <HbsButton
              id="new-binder-document-button"
              title="Add a new document to this binder"
              onClick={() => navigate(`/orgs/${orgId}/binder/${categoryId}/documents/new`)}
              leadingIcon={<Icon name="plus" size={14} />}
            >
              New document
            </HbsButton>
            <HbsButton
              id="new-binder-list-button"
              title="Add a new list to this binder"
              variant="ghost"
              onClick={() => navigate(`/orgs/${orgId}/binder/${categoryId}/lists/new`)}
              leadingIcon={<Icon name="plus" size={14} />}
            >
              New list
            </HbsButton>
            <HbsButton
              id="new-binder-schedule-button"
              title="Add a new schedule to this binder"
              variant="ghost"
              onClick={() => navigate(`/orgs/${orgId}/binder/${categoryId}/schedules/new`)}
              leadingIcon={<Icon name="plus" size={14} />}
            >
              New schedule
            </HbsButton>
          </div>
        )}
      </div>

      {shown.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center text-hbs-text-mute gap-2 py-16">
          <Icon name={isContacts ? 'contact' : 'folder'} size={40} className="text-hbs-text-mute" />
          <p className="text-[13px] max-w-xs">
            {isContacts
              ? 'No shared contacts here yet. Contacts shared with this family without a binder will appear here.'
              : 'Nothing in this binder yet.'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((g) => (
            <BinderSection
              key={g.key}
              label={g.label}
              icon={g.icon}
              items={g.items}
              renderRow={renderRow}
            />
          ))}
        </div>
      )}
    </div>
  );
}
